// AI-Powered Price Estimation System
// Fully compliant with Amazon/Walmart TOS - uses OUR estimates, not scraped data

/**
 * Estimate price for a product using AI
 * @param {string} productName - Name of the product
 * @param {string} category - Product category (optional)
 * @returns {Promise<Object>} - Price estimation with disclaimer
 */
export async function estimateProductPrice(productName, category = null) {
  // This will call our Google AI when we integrate the real API
  // For now, using smart local estimation
  
  const normalizedName = productName.toLowerCase();
  
  // Smart price database based on common items
  const priceRanges = {
    // Dairy
    'milk': { min: 3.49, max: 5.99, avg: 4.49, category: 'Dairy' },
    'eggs': { min: 2.49, max: 4.99, avg: 3.49, category: 'Dairy' },
    'butter': { min: 3.99, max: 6.99, avg: 4.99, category: 'Dairy' },
    'cheese': { min: 4.99, max: 8.99, avg: 6.49, category: 'Dairy' },
    'yogurt': { min: 3.99, max: 7.99, avg: 5.49, category: 'Dairy' },
    
    // Produce
    'banana': { min: 0.49, max: 0.79, avg: 0.59, category: 'Produce' },
    'apple': { min: 3.99, max: 6.99, avg: 4.99, category: 'Produce' },
    'tomato': { min: 2.99, max: 5.99, avg: 3.99, category: 'Produce' },
    'lettuce': { min: 1.99, max: 3.99, avg: 2.49, category: 'Produce' },
    'potato': { min: 3.99, max: 6.99, avg: 4.99, category: 'Produce' },
    
    // Meat
    'chicken': { min: 7.99, max: 12.99, avg: 9.99, category: 'Meat' },
    'beef': { min: 9.99, max: 15.99, avg: 12.99, category: 'Meat' },
    'pork': { min: 6.99, max: 10.99, avg: 8.49, category: 'Meat' },
    
    // Pantry
    'bread': { min: 1.99, max: 4.99, avg: 2.99, category: 'Bakery' },
    'rice': { min: 8.99, max: 15.99, avg: 11.99, category: 'Pantry' },
    'pasta': { min: 1.29, max: 3.99, avg: 1.99, category: 'Pantry' },
    'cereal': { min: 3.99, max: 6.99, avg: 4.99, category: 'Pantry' },
    
    // Snacks & Candy
    'chocolate': { min: 1.49, max: 4.99, avg: 2.99, category: 'Snacks' },
    'chocolate bar': { min: 1.49, max: 3.99, avg: 2.49, category: 'Snacks' },
    'candy': { min: 0.99, max: 3.99, avg: 1.99, category: 'Snacks' },
    'cookies': { min: 2.99, max: 5.99, avg: 3.99, category: 'Snacks' },
    'chips': { min: 3.49, max: 6.99, avg: 4.49, category: 'Snacks' },
    'popcorn': { min: 2.49, max: 4.99, avg: 3.49, category: 'Snacks' },
    'nuts': { min: 4.99, max: 9.99, avg: 6.99, category: 'Snacks' },
    
    // Household
    'detergent': { min: 9.99, max: 19.99, avg: 12.99, category: 'Household' },
    'paper towel': { min: 12.99, max: 24.99, avg: 16.99, category: 'Household' },
    'tissue': { min: 14.99, max: 22.99, avg: 17.99, category: 'Household' },
    'soap': { min: 4.99, max: 8.99, avg: 5.99, category: 'Personal Care' },
    
    // Default ranges by category
    '_defaults': {
      'Dairy': { avg: 5.99 },
      'Produce': { avg: 4.99 },
      'Meat': { avg: 10.99 },
      'Bakery': { avg: 3.99 },
      'Pantry': { avg: 6.99 },
      'Frozen': { avg: 7.99 },
      'Household': { avg: 12.99 },
      'Personal Care': { avg: 7.99 },
      'Other': { avg: 9.99 }
    }
  };

  // Try to find exact match
  for (const [keyword, data] of Object.entries(priceRanges)) {
    if (keyword !== '_defaults' && normalizedName.includes(keyword)) {
      return {
        estimatedPrice: data.avg,
        priceRange: `$${data.min.toFixed(2)} - $${data.max.toFixed(2)}`,
        confidence: 'high',
        category: category || data.category,
        disclaimer: 'This is an estimate based on typical market prices. Actual prices may vary by location and retailer.',
        method: 'keyword_match'
      };
    }
  }

  // Fallback to category-based estimate
  const categoryDefault = priceRanges._defaults[category] || priceRanges._defaults['Other'];
  
  return {
    estimatedPrice: categoryDefault.avg,
    priceRange: `Typically $${(categoryDefault.avg * 0.7).toFixed(2)} - $${(categoryDefault.avg * 1.3).toFixed(2)}`,
    confidence: 'medium',
    category: category || 'Other',
    disclaimer: 'This is a general estimate. Actual prices may vary significantly by brand, size, and location.',
    method: 'category_fallback'
  };
}

/**
 * Get recommended store for a product
 * @param {string} productName - Name of the product
 * @param {string} category - Product category
 * @returns {string} - 'amazon' or 'walmart'
 */
export function recommendStore(productName, category) {
  // Fresh/perishable items that need to be bought in-store → Walmart
  // These don't benefit from Amazon shipping and aren't eligible for affiliate anyway
  const walmartCategories = ['Dairy', 'Produce', 'Meat', 'Bakery'];
  
  // Everything else → Amazon (affiliate commission, bulk deals, Prime delivery)
  // Pantry, Household, Personal Care, Electronics, Frozen, Books, Toys, Sports, Other
  const amazonCategories = ['Pantry', 'Household', 'Personal Care', 'Electronics', 'Books', 'Toys', 'Sports', 'Frozen', 'Snacks', 'Beverages', 'Other'];
  
  if (walmartCategories.includes(category)) {
    return {
      store: 'walmart',
      reason: 'Fresh items are best picked up in-store at Walmart',
      emoji: '🟦'
    };
  }
  
  if (amazonCategories.includes(category)) {
    return {
      store: 'amazon',
      reason: 'Great prices and fast delivery on Amazon',
      emoji: '🟧'
    };
  }
  
  // Default to Amazon — earns affiliate commission on qualifying purchases
  return {
    store: 'amazon',
    reason: 'Convenient delivery and competitive pricing on Amazon',
    emoji: '🟧'
  };
}

/**
 * Enhanced AI price estimation (placeholder for Google AI integration)
 * @param {string} productName - Full product name
 * @returns {Promise<Object>} - Detailed price estimation
 */
export async function aiEstimatePrice(productName) {
  // TODO: Integrate with Google AI Cloud Function when ready
  // For now, use local estimation
  
  const category = smartCategorize(productName);
  const baseEstimate = await estimateProductPrice(productName, category);
  const storeRec = recommendStore(productName, category);
  
  return {
    ...baseEstimate,
    recommendedStore: storeRec,
    aiAnalysis: `Based on "${productName}", I recommend ${storeRec.store === 'amazon' ? 'Amazon' : 'Walmart'}. ${storeRec.reason}`
  };
}

/**
 * Smart categorization helper
 * @param {string} productName - Product name
 * @returns {string} - Category
 */
function smartCategorize(productName) {
  const name = productName.toLowerCase();
  
  const categories = {
    'Dairy': ['milk', 'cheese', 'butter', 'yogurt', 'cream', 'egg'],
    'Produce': ['apple', 'banana', 'orange', 'lettuce', 'tomato', 'potato', 'onion', 'carrot', 'fruit', 'vegetable'],
    'Meat': ['chicken', 'beef', 'pork', 'fish', 'turkey', 'bacon', 'sausage', 'steak'],
    'Bakery': ['bread', 'bagel', 'roll', 'donut', 'cake', 'muffin', 'cookie'],
    'Pantry': ['rice', 'pasta', 'flour', 'sugar', 'oil', 'cereal', 'oatmeal'],
    'Frozen': ['ice cream', 'pizza', 'frozen', 'popsicle', 'burrito'],
    'Household': ['paper towel', 'tissue', 'cleaner', 'detergent', 'soap', 'bleach', 'sponge'],
    'Personal Care': ['shampoo', 'toothpaste', 'deodorant', 'lotion', 'razor'],
    'Electronics': ['phone', 'laptop', 'tablet', 'headphone', 'charger', 'cable'],
    'Toys': ['lego', 'doll', 'action figure', 'puzzle', 'game'],
    'Books': ['book', 'novel', 'magazine', 'comic'],
    'Sports': ['ball', 'bike', 'fitness', 'yoga', 'weights']
  };

  for (const [category, keywords] of Object.entries(categories)) {
    if (keywords.some(keyword => name.includes(keyword))) {
      return category;
    }
  }

  return 'Other';
}

/**
 * Format price for display
 * @param {number} price - Price amount
 * @returns {string} - Formatted price
 */
export function formatPrice(price) {
  return `$${parseFloat(price).toFixed(2)}`;
}

/**
 * Get price estimation disclaimer text
 * @returns {string} - Disclaimer text
 */
export function getPriceDisclaimer() {
  return "💡 This is an AI-generated estimate based on typical market prices. Actual prices may vary by location, retailer, brand, and current promotions. Always check the retailer's website for current pricing.";
}


